import { OrderError } from '@seek/payments-widget';
import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import React, { useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { refreshButtonClicked } from 'src/app/analytics/refresh';
import {
  CONNECTION_ERROR,
  CONNECTION_SESSION_EXPIRED_ERROR,
} from 'src/app/constants/errorMessages';
import {
  ROUTE_REFRESH_ERROR,
  ROUTE_REFRESH_SUCCESS,
} from 'src/app/constants/routes';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { JOB_REFRESH_AD_PRESSED } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateThinking } from 'src/app/state/modules/app/thinking';
import { actionUpdateIsJobPosted } from 'src/app/state/modules/session/isJobPosted';
import { actionUpdateIsPaymentInProgress } from 'src/app/state/modules/session/isPaymentInProgress';
import { actionUpdateSeekListingId } from 'src/app/state/modules/tealium/seekListingId';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { log } from 'src/app/utils/logging';
import { OrderSummary } from 'src/app/views/components/OrderSummary';
import { RefreshFlowDataContext } from 'src/app/views/context/cajaFlowData';
import { useCreateRefreshedJobAndOrder } from 'src/app/views/hooks/useCreateRefreshedJobAndOrder';
import { useErrorType } from 'src/app/views/hooks/useErrorType';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import { BasicPageLayout } from 'src/app/views/layouts/BasicPageLayout';
import { PaymentsSection } from 'src/app/views/pages/Refresh/sections/PaymentsSection';
import { PageSection } from 'src/app/views/sections/PageSection';
import { BenefitList } from 'src/app/views/shared/BenefitList';
import { OverlayLoader } from 'src/app/views/shared/OverlayLoader';
import type AppState from 'src/types/appState';

import translations from './.vocab';

export const RefreshPage = () => {
  const { t } = useTranslations(translations);
  const {
    billToAgent,
    isDataCleanupComplete,
    jobId,
    orderId,
    seekAdvertiserId,
    tealium,
  } = useSelector(
    (state: AppState) => ({
      billToAgent: state.session.billToAgent,
      isDataCleanupComplete: state.app?.isDataCleanupComplete,
      jobId: state.session.job?.id,
      orderId: state.session.jobOrderId,
      seekAdvertiserId: state.session.seekAdvertiserId,
      tealium: state.tealium,
    }),
    shallowEqual,
  );
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);
  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);
  const [isRefreshInProgress, setIsRefreshInProgress] =
    useState<boolean>(false);

  const dispatch = useDispatch();

  const { loading: isRefreshDataLoading } = useContext(RefreshFlowDataContext);

  const { goToRoute } = useLocalisedRoutes();

  const createPendingJob = useCreateRefreshedJobAndOrder();

  useEffect(() => {
    if (isDataCleanupComplete && isPerUnitBilling) {
      goToRoute(ROUTE_REFRESH_ERROR);
    }
  }, [isDataCleanupComplete, isPerUnitBilling, goToRoute]);

  useEffect(() => {
    if (isDataCleanupComplete && seekAdvertiserId) {
      createPendingJob().then((response) => {
        if (response === null) {
          goToRoute(ROUTE_REFRESH_ERROR);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataCleanupComplete, seekAdvertiserId]);

  const { errorType, setError } = useErrorType();

  const confirmInvoiceOrderInProgress = (inProgress: boolean) => {
    setIsRefreshInProgress(inProgress);
    dispatch(actionUpdateThinking(inProgress));
  };

  const onSuccess = () => {
    dispatch(actionUpdateSeekListingId(String(jobId)));
    dispatch(actionUpdateIsJobPosted(true));

    goToRoute(ROUTE_REFRESH_SUCCESS);
  };

  const handledCancelPayment = () => {
    dispatch(actionUpdateIsPaymentInProgress(false));
  };

  const onError = (error: Error) => {
    confirmInvoiceOrderInProgress(false);

    const metadata = {
      error,
      billToAgent,
      isDataCleanupComplete,
      jobId,
      orderId,
      seekAdvertiserId,
    };

    if (error instanceof OrderError) {
      setError(CONNECTION_SESSION_EXPIRED_ERROR);

      log.error('Refresh | ConfirmInvoiceOrder | onOrderError', metadata);
      return;
    }

    log.error('Refresh | ConfirmInvoiceOrder | onError', metadata);

    setError(CONNECTION_ERROR);
    return;
  };

  const onButtonClicked = async () => {
    if (isRefreshInProgress) {
      return;
    }

    confirmInvoiceOrderInProgress(true);

    if (isPageAnalyticsEnabled) {
      const eventData = {
        eventName: JOB_REFRESH_AD_PRESSED,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      sendLinkEvent(tealium, refreshButtonClicked);
    }
  };

  return isRefreshDataLoading ? (
    <OverlayLoader />
  ) : (
    <BasicPageLayout title={t('refresh-page-title')} errorType={errorType}>
      <Stack space="medium">
        <PageSection heading={t('refresh-page-benefit-section-heading')}>
          <Stack space="large">
            <BenefitList
              items={[
                t('refresh-page-benefit-item-1'),
                t('refresh-page-benefit-item-2'),
                t('refresh-page-benefit-item-3'),
              ]}
            />
            <Text>{t('refresh-page-benefit-section-text')}</Text>
          </Stack>
        </PageSection>

        <OrderSummary hidePaymentDetails={false} />

        <PaymentsSection
          onPaymentSuccess={onSuccess}
          onPaymentFailed={onError}
          handledCancelPayment={handledCancelPayment}
          onPostAdButtonClicked={onButtonClicked}
          onPostAdButtonClickFailed={onError}
        />
      </Stack>
    </BasicPageLayout>
  );
};
